import React, { useState, useMemo } from 'react'
import { usePeople } from '../../hooks/usePeople'
import { useToast } from '../../hooks/useToast'
import PersonCombobox from '../common/PersonCombobox'
import ToastContainer from '../common/ToastContainer'
import ConfirmDialog from '../common/ConfirmDialog'

export default function FamilyAdmin({ user, familyId }) {
  const { people, createFamily, addChild: addChildRelation, removeChild: removeChildRelation, removeSpouse, refetch } = usePeople(user, familyId)
  const toast = useToast()
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  
  // Edit mode state
  const [editingFamilyId, setEditingFamilyId] = useState(null)
  const [editChildren, setEditChildren] = useState([])
  const [childToAddEdit, setChildToAddEdit] = useState(null)
  const [isSavingFamily, setIsSavingFamily] = useState(false)
  const [isDeletingFamily, setIsDeletingFamily] = useState(null)
  const [confirmDeleteFamily, setConfirmDeleteFamily] = useState(null)
  
  // Build family data from embedded relationships
  const families = useMemo(() => {
    const peopleMap = new Map(people.map(p => [p.id, p]))
    const processedCouples = new Set()
    const familyList = []

    people.forEach(person => {
      const spouses = person.spouses || []

      spouses.forEach(spouseId => {
        // Create unique key for couple (sorted to avoid duplicates)
        const coupleKey = [person.id, spouseId].sort().join('__')

        // Skip if we've already processed this couple
        if (processedCouples.has(coupleKey)) return
        processedCouples.add(coupleKey)

        const spouse = peopleMap.get(spouseId)
        if (!spouse) return

        // Find shared children
        const personChildren = new Set(person.children || [])
        const spouseChildren = spouse.children || []
        const sharedChildIds = spouseChildren.filter(childId => personChildren.has(childId))
        const childrenData = sharedChildIds.map(id => peopleMap.get(id)).filter(Boolean)

        familyList.push({
          id: coupleKey,
          spouse1: person,
          spouse2: spouse,
          children: childrenData,
          notes: []
        })
      })
    })

    return familyList
  }, [people])
  
  // Pagination
  const totalPages = Math.ceil(families.length / itemsPerPage)
  const paginatedFamilies = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage
    return families.slice(start, start + itemsPerPage)
  }, [families, currentPage, itemsPerPage])

  // Form state
  const [spouse1, setSpouse1] = useState(null)
  const [spouse2, setSpouse2] = useState(null)
  const [children, setChildren] = useState([])
  const [childToAdd, setChildToAdd] = useState(null)
  const [isCreatingFamily, setIsCreatingFamily] = useState(false)

  const addChild = () => {
    if (!childToAdd) {
      toast.error('Please select a person to add as child')
      return
    }
    if (children.find((c) => c.id === childToAdd.id)) {
      toast.error('This child is already added')
      return
    }
    setChildren((prev) => [...prev, childToAdd])
    setChildToAdd(null)
  }

  const removeChildFromForm = (idx) => {
    setChildren((prev) => prev.filter((_, i) => i !== idx))
  }
  
  const startEditFamily = (family) => {
    setEditingFamilyId(family.id)
    setEditChildren([...family.children])
    setChildToAddEdit(null)
  }
  
  const cancelEditFamily = () => {
    setEditingFamilyId(null)
    setEditChildren([])
    setChildToAddEdit(null)
  }
  
  const addChildToEditFamily = () => {
    if (!childToAddEdit) {
      toast.error('Please select a child to add')
      return
    }
    if (editChildren.find(c => c.id === childToAddEdit.id)) {
      toast.error('This child is already in the family')
      return
    }
    setEditChildren([...editChildren, childToAddEdit])
    setChildToAddEdit(null)
  }
  
  const removeChildFromEditFamily = (childId) => {
    setEditChildren(editChildren.filter(c => c.id !== childId))
  }
  
  const saveFamily = async (family) => {
    setIsSavingFamily(true)
    
    try {
      // Get current and new child IDs
      const originalChildIds = new Set(family.children.map(c => c.id))
      const newChildIds = new Set(editChildren.map(c => c.id))
      
      // Find children to add (in new but not in original)
      const childrenToAdd = editChildren.filter(c => !originalChildIds.has(c.id))
      
      // Find children to remove (in original but not in new)
      const childrenToRemove = family.children.filter(c => !newChildIds.has(c.id))
      
      // Add new children to both parents
      for (const child of childrenToAdd) {
        await addChildRelation(family.spouse1.id, child.id)
        await addChildRelation(family.spouse2.id, child.id)
      }
      
      // Remove children from both parents
      for (const child of childrenToRemove) {
        await removeChildRelation(family.spouse1.id, child.id)
        await removeChildRelation(family.spouse2.id, child.id)
      }
      
      setEditingFamilyId(null)
      setEditChildren([])
      toast.success('Family updated successfully!')
    } catch (err) {
      toast.error(`Error updating family: ${err.message}`)
      // Partial failure: some child ops may have succeeded, re-fetch Firestore truth
      refetch()
    } finally {
      setIsSavingFamily(false)
    }
  }
  
  const deleteFamily = async (family) => {
    setIsDeletingFamily(family.id)

    try {
      // Remove spouse relationship
      await removeSpouse(family.spouse1.id, family.spouse2.id)

      // Remove all shared children from both parents
      for (const child of family.children) {
        await removeChildRelation(family.spouse1.id, child.id)
        await removeChildRelation(family.spouse2.id, child.id)
      }

      toast.success('Family deleted successfully!')
    } catch (err) {
      toast.error(`Error deleting family: ${err.message}`)
      refetch()
    } finally {
      setIsDeletingFamily(null)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsCreatingFamily(true)

    try {
      if (!spouse1 || !spouse2) {
        toast.error('Both spouses are required')
        return
      }

      // Create the family using the new embedded structure
      const childIds = children.map(c => c.id)
      await createFamily(spouse1.id, spouse2.id, childIds)

      // Reset form
      const familyName = `${spouse1.name} & ${spouse2.name}`
      setSpouse1(null)
      setSpouse2(null)
      setChildren([])
      setChildToAdd(null)
      toast.success(`Family created successfully: ${familyName}`)
    } catch (err) {
      toast.error('Error creating family: ' + err.message)
    } finally {
      setIsCreatingFamily(false)
    }
  }

  return (
    <>
      <ToastContainer toasts={toast.toasts} removeToast={toast.removeToast} />
      <ConfirmDialog
        open={!!confirmDeleteFamily}
        onClose={() => setConfirmDeleteFamily(null)}
        onConfirm={() => deleteFamily(confirmDeleteFamily)}
        title="Delete Family"
        message={confirmDeleteFamily ? `Delete the family of ${confirmDeleteFamily.spouse1.name} & ${confirmDeleteFamily.spouse2.name}? This will remove their marriage and unlink their children.` : ''}
        confirmLabel="Delete"
        danger
      />
      <div className="space-y-6">
      {/* Create Family Form - Collapsible */}
      <div className="glass-card p-6">
        <details className="group" open>
          <summary className="cursor-pointer list-none flex items-center justify-between">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
              Create New Family
            </h2>
            <span className="text-gray-500 dark:text-gray-400 group-open:rotate-180 transition-transform">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </summary>
          
          <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="glass-label text-sm">
                  Spouse 1 <span className="text-red-500">*</span>
                </label>
                <PersonCombobox
                  people={people}
                  value={spouse1}
                  onChange={setSpouse1}
                  placeholder="Select spouse 1..."
                />
              </div>
              <div>
                <label className="glass-label text-sm">
                  Spouse 2 <span className="text-red-500">*</span>
                </label>
                <PersonCombobox
                  people={people}
                  value={spouse2}
                  onChange={setSpouse2}
                  placeholder="Select spouse 2..."
                />
              </div>
            </div>


            <div>
              <label className="glass-label text-sm">Add Children (Optional)</label>
              <div className="flex gap-3">
                <div className="flex-1">
                  <PersonCombobox
                    people={people}
                    value={childToAdd}
                    onChange={setChildToAdd}
                    placeholder="Select child..."
                  />
                </div>
                <button type="button" onClick={addChild} className="glass-btn-secondary text-sm">
                  <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add
                </button>
              </div>
            </div>

            {children.length > 0 && (
              <div>
                <label className="glass-label text-sm">Children Selected ({children.length}):</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {children.map((child, idx) => (
                    <span
                      key={child.id}
                      className="glass-badge inline-flex items-center gap-2 cursor-pointer hover:bg-red-500/20 hover:text-red-700 dark:hover:text-red-300 hover:border-red-500/30 transition-all"
                      onClick={() => removeChildFromForm(idx)}
                      title="Click to remove"
                    >
                    {child.name} <span className="text-lg leading-none">&times;</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 items-start pt-3">
              <button 
                type="submit" 
                className="glass-btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isCreatingFamily}
              >
                {isCreatingFamily ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 inline" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create Family
                  </>
                )}
              </button>
            </div>
          </form>
        </details>
      </div>

      {/* Families Table */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
            All Families ({families.length})
          </h2>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-300/30 dark:border-gray-600/30">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Spouse 1</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Spouse 2</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Children</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedFamilies.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No families yet. Create one above!
                  </td>
                </tr>
              ) : (
                paginatedFamilies.map((family) => (
                  <tr key={family.id} className="border-b border-gray-200/30 dark:border-gray-700/30 hover:bg-white/30 dark:hover:bg-gray-700/30 transition-colors">
                    {editingFamilyId === family.id ? (
                      <>
                        <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                          {family.spouse1?.name || '?'}
                        </td>
                        <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                          {family.spouse2?.name || '?'}
                        </td>
                        <td className="py-3 px-4">
                          <div className="space-y-2">
                            <div className="flex gap-2">
                              <div className="flex-1">
                                <PersonCombobox
                                  people={people}
                                  value={childToAddEdit}
                                  onChange={setChildToAddEdit}
                                  placeholder="Add child..."
                                />
                              </div>
                              <button
                                type="button"
                                onClick={addChildToEditFamily}
                                className="glass-btn-secondary glass-btn-sm whitespace-nowrap"
                              >
                                <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                              </button>
                            </div>
                            {editChildren.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {editChildren.map(child => (
                                  <span
                                    key={child.id}
                                    className="glass-badge text-xs inline-flex items-center gap-1 cursor-pointer hover:bg-red-500/20 hover:text-red-700 dark:hover:text-red-300 hover:border-red-500/30 transition-all"
                                    onClick={() => removeChildFromEditFamily(child.id)}
                                    title="Click to remove"
                                  >
                                    {child.name} <span className="text-base leading-none">&times;</span>
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">No children</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => saveFamily(family)}
                              className="glass-btn-primary glass-btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Save"
                              disabled={isSavingFamily}
                            >
                              {isSavingFamily ? (
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </button>
                            <button
                              onClick={cancelEditFamily}
                              className="glass-btn-secondary glass-btn-sm"
                              title="Cancel"
                              disabled={isSavingFamily}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                          {family.spouse1?.name || '?'}
                        </td>
                        <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                          {family.spouse2?.name || '?'}
                        </td>
                        <td className="py-3 px-4">
                          {family.children.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {family.children.map(child => (
                                <span key={child.id} className="glass-badge text-xs">
                                  {child.name}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">No children</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => startEditFamily(family)}
                              className="glass-btn-secondary glass-btn-sm"
                              title="Edit Children"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => setConfirmDeleteFamily(family)}
                              className="glass-btn-danger glass-btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Delete Family"
                              disabled={isDeletingFamily === family.id}
                            >
                              {isDeletingFamily === family.id ? (
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              )}
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-300/30 dark:border-gray-600/30">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, families.length)} of {families.length}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="glass-btn-secondary glass-btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              {[...Array(totalPages)].map((_, i) => {
                const page = i + 1
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`glass-btn-sm min-w-[2rem] ${
                        currentPage === page
                          ? 'glass-btn-primary'
                          : 'glass-btn-secondary'
                      }`}
                    >
                      {page}
                    </button>
                  )
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="text-gray-500">...</span>
                }
                return null
              })}
              
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="glass-btn-secondary glass-btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  )
}
